import DailyIframe from "@daily-co/daily-js";
import { createSlice } from "@reduxjs/toolkit";
import DeviceIssueTip from "components/Home/Modals/DeviceIssueTip/DeviceIssueTip";
import { NETWORK_MONITOR_INTERVAL } from "constants/constants";
import { testDontConnectToRoom, testDummyMedia } from "helpers/dev";
import {
  startMeasuringMicLevel,
  stopMeasuringMicLevel,
} from "helpers/media/soundmeter";
import { waiter } from "helpers/randomHelpers";
import { sendTalking, sendPinnedShare } from "./channels/spaceChannelSlice";
import {
  enumerateAllDevices,
  enumerateAudioInputs,
  enumerateVideoInputs,
  selectCurrentCamera,
  selectCurrentMic,
  selectCurrentSpeakers,
  setCamAllowed,
  setCurrentMic,
  setCurrentCamera,
  setCurrentSpeakers,
  setMicAllowed,
  selectCurrentCameraTrackId,
  setCurrentMicTrack,
  setCurrentCameraTrack,
  clearCurrentCameraTrack,
  clearCurrentMicTrack,
} from "./devicesSlice";
import { setGeneralModal } from "./generalSlice";
import {
  GRID_VIEW,
  PINNED_CAMERA,
  PINNED_SHARE,
  selectBigLuids,
  selectMeFocus,
  selectMePinnedShare,
  selectMyLuid,
} from "./spaceMediaSlice";
import {
  graphicUpdateMyDevices,
  selectMeScreenTrackId,
  selectMeSharing,
  setMeCamera,
  setMeMic,
  setMeScreenTrackId,
  setMeSharing,
} from "./spaceMyMediaSlice";
import {
  leaveSpace,
  turnOffBoard,
  turnOffFocus,
  turnOffPinnedShare,
  turnOffShare,
} from "./spaceSlice";

let dispatch = null;
let getState = null;
import("Store").then((module) => {
  dispatch = module.store.dispatch;
  getState = module.store.getState;
});

const INITIAL_STATE = {
  networkQuality: 2,
  networkConnection: true,
  meetingLive: false,
};

let participantUpdatedQueue = {};
let shareUpdatedQueue = {};
let callFrame = null;

let sessionIds = {};
window.getSessionIds = () => {
  return sessionIds;
};

export const slice = createSlice({
  name: "mediaServer",
  initialState: INITIAL_STATE,
  reducers: {
    setNetworkQuality: (state, action) => {
      state.networkQuality = action.payload;
    },
    setNetworkConnection: (state, action) => {
      state.networkConnection = action.payload;
    },
    setMeetingLive: (state, action) => {
      state.meetingLive = action.payload;
    },
    resetMediaServerSlice: () => INITIAL_STATE,
  },
});

export const selectNetworkQuality = (state) => state.mediaServer.networkQuality;
export const selectNetworkConnection = (state) =>
  state.mediaServer.networkConnection;
export const selectMeetingLive = (state) => state.mediaServer.meetingLive;

export const {
  setNetworkQuality,
  setNetworkConnection,
  setMeetingLive,
  resetMediaServerSlice,
} = slice.actions;

//#region // CallFrame Events Handlers
function handleCallFrameLoaded() {
  window.efs_debug && window.log("CallFrame successfully loaded");
}
function handleCallFrameLoadFailed() {
  window.efs_debug && window.log("CallFrame failed to load");
}
//#endregion

//#region // Network Connectivity handlers
const handleNetworkConnection = ({ event }) => {
  window.efs_debug && window.log("network-connection");
  window.efs_debug && window.log(event);
  if (event === "interrupted") {
    dispatch(setNetworkConnection(false));
  } else if (event === "connected") {
    if (!selectNetworkConnection(getState())) {
      dispatch(setNetworkConnection(true));
    }
  }
};
const handleNetworkQualityChange = ({ quality }) => {
  window.efs_debug && window.log("network-quality-change");
  window.efs_debug && window.log(quality);
  //   // TODO
  //   // relay to bar in top right maybe
  //   window.efs_debug && window.log("network quality change func");
  //   window.efs_debug && window.log(quality);
  //   let networkQuality = 0;
  //   if (quality < 25) networkQuality = 1;
  //   else if (quality < 50) networkQuality = 2;
  //   else if (quality < 75) networkQuality = 3;
  //   else networkQuality = 4;
  //   dispatch(setNetworkQuality(networkQuality));
};
// #endregion

//#region // Meeting entering and leaving handlers
const handleJoiningMeeting = () => {
  window.efs_debug && window.log("In the process of joining a call");
  dispatch(setMeetingLive(false));
};
const handleJoinedMeeting = async ({ participants }) => {
  window.efs_debug && window.log("Now in a call");
  window.efs_debug && window.log(participants);

  for (let id in participants) {
    sessionIds[participants[id].user_id] = id;
    if (!participants[id].local) {
      participantUpdatedQueue[id] = participants[id];
    } else {
      handleLocalParticipantChanges(participants[id]);
    }
  }
  dispatch(setMeetingLive(true));
  startMonitoringNetwork();

  await enumerateAllDevices();
  setDailyDevices();
};
const handleLeftMeeting = () => {
  window.efs_debug && window.log("Left the meeting");
  dispatch(setMeetingLive(false));
};
const handleErrorMeeting = ({ errorMsg }) => {
  window.efs_debug && window.log("Booted from the meeting due to error: ");
  window.efs_debug && window.log(errorMsg);
  dispatch(setMeetingLive(false));
  leaveSpace();
};
//#endregion

//#region // Meeting Track and Device Updates handlers
const handleCameraError = (e) => {
  let { errorMsg } = e;
  window.efs_debug && window.log("camera-error: ");
  window.efs_debug && window.log(e);
  switch (errorMsg.errorMsg) {
    case "devices Error":
      let content = " could not be found.";
      if (!errorMsg.audioOk && !errorMsg.videoOk)
        content = "Camera and microphone" + content;
      else if (!errorMsg.audioOk) content = "Microphone" + content;
      else if (!errorMsg.videoOk) {
        content = "Camera" + content;
        dispatch(setMeCamera(false));
      } else content = "Device" + content;
      dispatch(
        setGeneralModal({ title: "Devices Not Detected", content: content })
      );
      break;
    case "camera/mic error":
      dispatch(
        setGeneralModal({
          title: "Device Error",
          content: "Could not establish a microphone or camera to use.",
        })
      );
      break;
    case "not allowed":
      let error = e.error;
      if (
        error?.type &&
        ((error.type && error.type === "cam-mic-in-use") ||
          error.type === "mic-in-use" ||
          error.type === "cam-in-use")
      ) {
        let devices = "devices";
        if (error.type === "cam-mic-in-use") {
          devices = "camera and microphone";
          dispatch(setMeCamera(false));
          dispatch(setMeMic(false));
        } else if (error.type === "mic-in-use") {
          devices = "microphone";
          dispatch(setMeMic(false));
        } else if (error.type === "cam-in-use") {
          devices = "camera";
          dispatch(setMeCamera(false));
        }
        dispatch(
          setGeneralModal({
            title: "Device Already In Use",
            content:
              "Your " +
              devices +
              " is being used by another application. Please try again once they are free.",
          })
        );
      } else {
        dispatch(
          setGeneralModal({
            title: "Access to the camera or microphone is blocked!",
            content: (
              <>
                You can adjust your microphone and camera permissions in your
                browser or system settings. <br />
                <br />
                <DeviceIssueTip />
              </>
            ),
          })
        );
      }
      break;
    default:
      window.efs_debug && window.log("errorMsg did not match");
      break;
  }
};

// track started and track stopped events dont say if its screen or camera feed
const handleTrackStarted = (e) => {
  window.efs_debug && window.log("track started");
  window.efs_debug && window.log(e);
  if (e.participant.local) handleLocalTrackStarted(e);
  else handleRemoteTrackStarted(e);
};
const handleTrackStopped = (e) => {
  window.efs_debug && window.log("track stopped");
  window.efs_debug && window.log(e);
  if (e.participant?.local) handleLocalTrackStopped(e);
  else handleRemoteTrackStopped(e);
};
// const handleActiveSpeakerChange = (peer_id) => {
//   window.efs_debug && window.log("Actie-Speaker-Change");
//   window.efs_debug && window.log(e);
//   // TODO?
// };
//#endregion

//#region // Meeting Participant Handlers
const handleParticipantJoined = ({ participant }) => {
  window.efs_debug && window.log("remote participant joined");
  window.efs_debug && window.log(participant);
  if (!getState().mediaServer.meetingLive) return;
  sessionIds[participant.user_id] = participant.session_id;
  if (!participant.local) {
    participantUpdatedQueue[participant.session_id] = participant;
  } else {
    handleLocalParticipantChanges(participant);
  }
};
const handleParticipantUpdated = ({ participant }) => {
  window.efs_debug && window.log("remote OR local participant updated");
  // window.efs_debug && window.log(JSON.stringify(participant));
  if (!participant.local) {
    participantUpdatedQueue[participant.session_id] = participant;
  } else {
    handleLocalParticipantChanges(participant);
  }
};

const handleParticipantLeft = ({ participant }) => {
  window.efs_debug && window.log("remote participant left");
  window.efs_debug && window.log(participant);
  // TODO?
};
//#endregion

export function loadCallFrame() {
  if (callFrame) {
    window.efs_debug && window.log("CallFrame Already loaded");
    return;
  }
  callFrame = DailyIframe.createCallObject();
  window.callFrame = callFrame;
  //#region // CALLFRAME EVENT HANDLERS
  // CallFrame events
  callFrame.on("loaded", handleCallFrameLoaded);
  callFrame.on("load-attempt-failed", handleCallFrameLoadFailed);

  // Meeting entering and leaving events
  callFrame.on("joining-meeting", handleJoiningMeeting);
  callFrame.on("joined-meeting", handleJoinedMeeting);
  callFrame.on("left-meeting", handleLeftMeeting);
  callFrame.on("error", handleErrorMeeting);

  // Participant, Track and Device Updates
  callFrame.on("camera-error", handleCameraError);
  callFrame.on("participant-joined", handleParticipantJoined);
  callFrame.on("participant-updated", handleParticipantUpdated);
  callFrame.on("participant-left", handleParticipantLeft);
  callFrame.on("track-started", handleTrackStarted);
  callFrame.on("track-stopped", handleTrackStopped);
  // callFrame.on("active-speaker-change", handleActiveSpeakerChange);

  // Network Connectivity Events
  callFrame.on("network-connection", handleNetworkConnection);
  callFrame.on("network-quality-change", handleNetworkQualityChange);
  //#endregion
}

export function unloadCallFrame() {
  //#region // CALLFRAME EVENT HANDLERS
  // CallFrame events
  if (!callFrame) {
    window.efs_debug && window.log("CallFrame was not already loaded");
    return;
  }
  callFrame.off("loaded", handleCallFrameLoaded);
  callFrame.off("load-attempt-failed", handleCallFrameLoadFailed);

  // Meeting entering and leaving events
  callFrame.off("joining-meeting", handleJoiningMeeting);
  callFrame.off("joined-meeting", handleJoinedMeeting);
  callFrame.off("left-meeting", handleLeftMeeting);
  callFrame.off("error", handleErrorMeeting);

  // Participant, Track and Device Updates
  callFrame.off("camera-error", handleCameraError);
  callFrame.off("participant-joined", handleParticipantJoined);
  callFrame.off("participant-updated", handleParticipantUpdated);
  callFrame.off("participant-left", handleParticipantLeft);
  callFrame.off("track-started", handleTrackStarted);
  callFrame.off("track-stopped", handleTrackStopped);
  // callFrame.off("active-speaker-change", handleActiveSpeakerChange);

  // Network Connectivity Events
  callFrame.off("network-connection", handleNetworkConnection);
  callFrame.off("network-quality-change", handleNetworkQualityChange);
  //#endregion
  window.efs_debug && window.log("Calling CallFrame to destruct");
  stopMonitoringNetwork();
  callFrame
    .destroy()
    .then(() => {
      window.efs_debug && window.log("CallFrame destroyed itself");
      callFrame = null;
      window.callFrame = null;
    })
    .catch(() => {
      window.efs_debug && window.log("CallFrame Could not destroy itself!");
    });
}

export function handleOtherShare(luid, value) {
  shareUpdatedQueue[luid] = value;
}

export function gatherAnyDeviceChanges() {
  let updates = false;
  let people = null; // dont initialise this unless we need it
  let deviceChanges = {};
  window.efs_debug && window.log("Participant Updated Queue", 1);
  window.efs_debug && window.log(participantUpdatedQueue, 1);

  for (let session_id in participantUpdatedQueue) {
    let luid = participantUpdatedQueue[session_id].user_id;
    if (people === null) people = { ...getState().spaceMedia.people };
    let { micOn, camOn, screenOn } = handleRemoteParticipantChanges(
      {
        ...people[luid],
      },
      participantUpdatedQueue[session_id]
    );
    deviceChanges[luid] = {
      micOn,
      share:
        people[luid]?.share === "board" ? "board" : screenOn ? "screen" : null, // Since there is a divide between outsourced video conferencing and our own servers, eventually there will be only one state kept consistent with both client and the single signalling server
      camOn,
    };
    updates = true;
  }

  for (let luid in shareUpdatedQueue) {
    if (people === null) people = { ...getState().spaceMedia.people };

    let share =
      shareUpdatedQueue[luid] === "board"
        ? "board"
        : people[luid].screenOn
        ? "screen"
        : null;

    if (deviceChanges[luid]) deviceChanges[luid].share = share;
    else deviceChanges[luid] = { share };

    updates = true;
  }

  if (updates) {
    participantUpdatedQueue = {};
    shareUpdatedQueue = {};
    return deviceChanges;
  } else return null;
}

export const checkMediaConferenceSupported = () =>
  DailyIframe.supportedBrowser().supported;

export const determineRemoteDeviceOn = (trackState) => {
  return (
    trackState === "sendable" ||
    trackState === "playable" ||
    trackState === "loading" ||
    trackState === "interrupted"
    //causing problems when turning on mic and mic is blocked, it will appear as interrupted
  );
};

let sentTalking = false; // to keep track so we dont send the same thing twice
const adjustMyMicElemvisual = (value) => {
  let elem = document.getElementById("mic-color-element");
  if (elem) {
    if (value > 0.006) {
      elem.setAttribute("class", "talking-mic-color");
      if (!sentTalking) {
        sentTalking = true;
        sendTalking(true);
      }
    } else {
      elem.setAttribute("class", "no-talk-mic-color");
      if (sentTalking) {
        sentTalking = false;
        sendTalking(false);
      }
    }
  } else window.efs_debug && window.log("mic color elem not found");
};

const handleLocalTrackStarted = ({ participant, track }) => {
  if (track.kind === "audio") {
    handleLocalAudioTrackStarted({ participant, track });
  } else if (track.kind === "video") {
    if (isAddedScreenTrack({ participant, track })) {
      handleLocalScreenTrackStarted({ track });
    } else if (isAddedCameraTrack({ participant, track })) {
      handleLocalCameraTrackStarted({ track });
    } else window.log("Track Video type could not be determined");
  } else window.log("Track type could not be determined");

  graphicUpdateMyDevices();
};
const handleLocalTrackStopped = ({ participant, track }) => {
  if (track.kind === "audio") {
    handleLocalAudioTrackStopped({ track });
  } else if (track.kind === "video") {
    if (isMyRemovedScreenTrack({ track })) {
      handleLocalScreenTrackStopped({ track });
    } else if (isMyRemovedCameraTrack({ track })) {
      handleLocalCameraTrackStopped({ participant });
    } else window.log("Track Video type could not be determined");
  } else window.log("Track type could not be determined");

  graphicUpdateMyDevices();
};

const handleRemoteTrackStarted = ({ participant, track }) => {
  if (track.kind === "audio") {
    handleRemoteAudioTrackStarted(participant.user_id, track);
  } else if (track.kind === "video") {
    if (isAddedScreenTrack({ participant, track })) {
      handleRemoteScreenTrackStarted(participant.user_id, track);
    } else if (isAddedCameraTrack({ participant, track })) {
      handleRemoteCameraTrackStarted(participant.user_id, track);
    } else window.log("Track Video type could not be determined");
  } else window.log("Track type could not be determined");
};

const handleRemoteTrackStopped = ({ participant, track }) => {};

//#region Track determiners
function isAddedScreenTrack({ participant, track }) {
  let trackType = track.id;
  let screenTrackId = participant.tracks.screenVideo?.persistentTrack?.id;
  return screenTrackId && trackType && trackType === screenTrackId;
}
function isAddedCameraTrack({ participant, track }) {
  let trackType = track.id;
  let cameraTrackId = participant.tracks.video?.persistentTrack?.id;
  return cameraTrackId && trackType && trackType === cameraTrackId;
}
function isMyRemovedCameraTrack({ track }) {
  return selectCurrentCameraTrackId(getState()) === track.id;
}
function isMyRemovedScreenTrack({ track }) {
  return selectMeScreenTrackId(getState()) === track.id;
}
//#endregion

//#region Local Track Handlers ////////////////////////////////////////
function handleLocalAudioTrackStarted({ track }) {
  setMicAllowed();
  setCurrentMicTrack(track);

  startMeasuringMicLevel(
    selectMyLuid(getState()),
    new MediaStream([track]),
    adjustMyMicElemvisual
  );
}

function handleLocalScreenTrackStarted({ track }) {
  dispatch(setMeScreenTrackId(track.id));
  // if (selectMeSharing(getState()) === null)
  dispatch(setMeSharing("screen"));
  if (selectMePinnedShare(getState())) sendPinnedShare(true);
  let elem = document.getElementById("myShareElement");
  elem.srcObject = null;
  elem.srcObject = new MediaStream([track]);
}

function handleLocalCameraTrackStarted({ track }) {
  setCamAllowed();
  setCurrentCameraTrack(track);

  let elem = document.getElementById("myCameraElement");
  elem.srcObject = null;
  elem.srcObject = new MediaStream([track]);
}

function handleLocalAudioTrackStopped() {
  clearCurrentMicTrack();
  stopMeasuringMicLevel(getState().spaceMedia.myLuid);
}

function handleLocalScreenTrackStopped() {
  // giving "board" state precendence over screen in case it turns on late
  if (selectMeSharing(getState()) === "screen") dispatch(setMeSharing(null));
  turnOffPinnedShare();
  dispatch(setMeScreenTrackId(null));
  document.getElementById("myShareElement").srcObject = null;
}

function handleLocalCameraTrackStopped() {
  clearCurrentCameraTrack();
  document.getElementById("myCameraElement").srcObject = null;
}
//#endregion

//#region Remote Track Handlers ///////////////////////////////////////
function handleRemoteAudioTrackStarted(luid, track) {
  let elem = document.getElementById(`remoteAudio${luid}`);
  if (!elem) return;

  elem.srcObject = null;
  elem.srcObject = new MediaStream([track]);
}

function handleRemoteScreenTrackStarted(luid, track) {
  let elem = document.getElementById(`remoteScreen${luid}`);
  if (!elem) return;

  let visual = getState().spaceMedia.people[luid]?.visual;
  if (visual) {
    if (visual === GRID_VIEW || visual === PINNED_SHARE)
      setBigScreenQuality(luid);
    else setSmallScreenQuality(luid);
  }
  elem.srcObject = null;
  elem.srcObject = new MediaStream([track]);
}

function handleRemoteCameraTrackStarted(luid, track) {
  let elem = document.getElementById(`remoteCamera${luid}`);
  if (!elem) return;

  let visual = getState().spaceMedia.people[luid]?.visual;
  if (visual) {
    if (visual === GRID_VIEW || visual === PINNED_CAMERA)
      setBigCamQuality(luid);
    else setSmallCamQuality(luid);
  }
  elem.srcObject = null;
  elem.srcObject = new MediaStream([track]);
}
//#endregion

export const handleLocalParticipantChanges = (myParticipant) => {
  window.efs_debug && window.log("Handle local participant changes");
  window.efs_debug && window.log(myParticipant);
  window.efs_debug && window.log("Finished local participant changes");
};

// Does not set state but changes person object
export const handleRemoteParticipantChanges = (person, participant) => {
  window.efs_debug && window.log("Handle remote participant changes");
  window.efs_debug && window.log(participant);
  person.micOn = determineRemoteDeviceOn(participant.tracks.audio.state);
  person.screenOn = determineRemoteDeviceOn(
    participant.tracks.screenVideo.state
  );
  person.camOn = determineRemoteDeviceOn(participant.tracks.video.state);

  if (person.micOn && !document.getElementById(`remoteAudio${person.luid}`))
    unsubscribeToAudio(person.luid);
  if (person.screenOn && !document.getElementById(`remoteScreen${person.luid}`))
    unsubscribeToScreen(person.luid);
  if (person.camOn && !document.getElementById(`remoteCamera${person.luid}`))
    unsubscribeToCamera(person.luid);

  return person;
};

let networkMonitorRef = null;
const startMonitoringNetwork = () => {
  networkMonitorRef = setInterval(() => {
    if (!selectMeetingLive(getState())) {
      stopMonitoringNetwork();
      return;
    }
    callFrame.getNetworkStats().then(({ stats }) => {
      window.efs_debug && window.log(stats);
      window.efs_debug && window.log(stats.latest.videoRecvBitsPerSecond);
      // handleNetworkQualityChange(stats);
    });
  }, NETWORK_MONITOR_INTERVAL);
};
const stopMonitoringNetwork = () => {
  clearInterval(networkMonitorRef);
  networkMonitorRef = null;
};

export const tryConnectToRoom = ({ room_url, token }) => {
  return new Promise(async (res, rej) => {
    loadCallFrame();

    if (testDontConnectToRoom) {
      await waiter(40);
      res(false);
      return;
    }

    if (room_url === null) {
      window.efs_debug && window.log("Room URL not received YET");
      res(false);
    } else {
      window.efs_debug &&
        window.log("TRYING TO CONNECT TO CONFERNCE WITH Room URL: " + room_url);
      try {
        await callFrame.join({
          url: room_url,
          token: token,
          subscribeToTracksAutomatically: false,
          audioSource: false,
          videoSource: false,
          dailyConfig: {
            experimentalChromeVideoMuteLightOff: true,
            screenSimulcastEncodings: [
              { maxBitrate: 80000, maxFramerate: 3, scaleResolutionDownBy: 2 },
              { maxBitrate: 600000, maxFramerate: 15 },
            ],
          },
        });
        await callFrame.setNetworkTopology({ topology: "sfu" });

        res(true);
      } catch (e) {
        console.error("ERROR while joining meeting", e);
        rej();
      }
    }
  });
};
export const disconnectFromRoom = async () => {
  window.efs_debug && window.log("Disconnecting from room.");
  stopMeasuringMicLevel(getState().spaceMedia.myLuid);
  //disengageMyAudioStream();
  if (callFrame) {
    window.efs_debug && window.log("Calling call frame leave.");
    // may need to check if meeting has been entered
    if (selectMeetingLive(getState())) {
      stopAllDevices();
      await callFrame.leave();
    }
  }
  dispatch(resetMediaServerSlice());
};

//#region // Device Manipulations
export const turnOffMic = () => {
  window.efs_debug && window.log("Turning OFF mic");
  dispatch(setMeMic(false));
  sendTalking(false);
  if (callFrame) callFrame.setLocalAudio(false);
  else callFrame.setLocalAudio(false);
};
export const turnOnMic = async () => {
  window.efs_debug && window.log("Turning ON mic");

  clearCurrentMicTrack();

  if (selectMeFocus(getState())) turnOffFocus();
  if (!selectCurrentMic(getState())) await enumerateAudioInputs();
  if (!selectCurrentMic(getState())) {
    window.efs_debug && window.log("Failed to find a mic");
    dispatch(setMeMic(false));
    return;
  }

  dispatch(setMeMic(true));
  callFrame.setLocalAudio(true);
};

export const turnOffScreen = () => {
  callFrame.stopScreenShare();
};
export const turnOnScreen = () => {
  if (selectMeFocus(getState())) turnOffFocus();
  if (selectMeSharing(getState()) === "board") turnOffBoard();

  if (!DailyIframe.supportedBrowser().supportsScreenShare) {
    dispatch(
      setGeneralModal({
        title: "Screen Sharing Not Supported",
        content:
          "This browser does not support sharing your computer screen. Please use a modern desktop browser.",
      })
    );
    return;
  }

  try {
    callFrame.startScreenShare();
  } catch (error) {
    window.log("Failed to Start Screen Share" + JSON.stringify(error));
  }
};

export const turnOffCamera = () => {
  dispatch(setMeCamera(false));
  callFrame.setLocalVideo(false);
};

export const turnOnCamera = async () => {
  clearCurrentCameraTrack();

  if (selectMeFocus(getState())) turnOffFocus();

  window.efs_debug && window.log("Turning ON cam");

  if (!selectCurrentCamera(getState())) await enumerateVideoInputs();

  if (!selectCurrentCamera(getState())) {
    window.efs_debug && window.log("Failed to find a Cam");
    dispatch(setMeCamera(false));
    return;
  }

  dispatch(setMeCamera(true));
  callFrame.setLocalVideo(true);
};

export function stopAllDevices() {
  if (selectMeetingLive(getState()) && callFrame) {
    turnOffMic();
    // For some reason this order is important:
    // cam before screen otherwise remote people don't get track stopped signal
    turnOffCamera();
    turnOffShare();
  }
}

export const isMicOnInMediaServer = () => callFrame && callFrame.localAudio();
export const isCamOnInMediaServer = () => callFrame && callFrame.localVideo();
//#endregion

//#region // Subscriptions to media
export const subscribeToCamera = (luid) => {
  window.efs_debug && window.log("subscribe to Camera: " + luid, 1);

  if (testDummyMedia) {
    let elem = document.getElementById(`remoteCamera${luid}`);
    if (elem) {
      elem.src = "http://127.0.0.1:8887/testVideoStuff.mov";
    } else {
      unsubscribeToCamera(luid);
    }
    return;
  }

  callFrame &&
    callFrame.updateParticipant(sessionIds[luid], {
      setSubscribedTracks: { video: true },
    });
};

export const unsubscribeToCamera = (luid) => {
  window.efs_debug && window.log("unsubscribe to camera: " + luid, 1);

  let elem = document.getElementById(`remoteCamera${luid}`);
  if (elem) {
    if (testDummyMedia) {
      // elem.src.stop();
      elem.src = null;
      return;
    } else {
      elem.srcObject = null;
    }
  }
  callFrame &&
    callFrame.updateParticipant(sessionIds[luid], {
      setSubscribedTracks: { video: false },
    });
};

export const subscribeToAudio = (luid) => {
  window.efs_debug && window.log("subscribe to audio: " + luid, 1);

  if (testDummyMedia) {
    // let elem = document.getElementById(`remoteAudio${luid}`);
    // if (elem) {
    //   elem.src = "http://127.0.0.1:8887/";
    // } else {
    //   unsubscribeToAudio(luid);
    // }
    return;
  }
  callFrame &&
    callFrame.updateParticipant(sessionIds[luid], {
      setSubscribedTracks: { audio: true },
    });
};

export const unsubscribeToAudio = (luid) => {
  window.efs_debug && window.log("unsubscribe to audio: " + luid, 1);

  let elem = document.getElementById(`remoteAudio${luid}`);
  if (elem) {
    if (testDummyMedia) {
      // elem.src.stop();
      elem.src = null;
      return;
    } else {
      elem.srcObject = null;
    }
  }
  callFrame &&
    callFrame.updateParticipant(sessionIds[luid], {
      setSubscribedTracks: { audio: false },
    });
};

export const subscribeToScreen = (luid) => {
  window.efs_debug && window.log("subscribe to screen: " + luid, 1);

  if (testDummyMedia) {
    let elem = document.getElementById(`remoteScreen${luid}`);

    if (elem) {
      elem.src = "http://127.0.0.1:8887/TestScreenRecordingSmall.mov";
    } else {
      unsubscribeToScreen(luid);
    }
    return;
  }
  callFrame &&
    callFrame.updateParticipant(sessionIds[luid], {
      setSubscribedTracks: { screenVideo: true },
    });
};

export const unsubscribeToScreen = (luid) => {
  window.efs_debug && window.log("unsubscribe to screen: " + luid, 1);

  let elem = document.getElementById(`remoteScreen${luid}`);
  if (elem) {
    if (testDummyMedia) {
      // elem.src.stop();
      elem.src = null;
      return;
    } else {
      elem.srcObject = null;
    }
  }
  callFrame &&
    callFrame.updateParticipant(sessionIds[luid], {
      setSubscribedTracks: { screenVideo: false },
    });
};
//#endregion

//#region // Setting Media Quality

export async function setBigCamQuality(luid) {
  let numBigs = selectBigLuids(getState()).length;
  if (numBigs > 6) setCamToLD(luid);
  else if (numBigs > 1) setCamToMD(luid);
  else setCamToHD(luid);
}

export async function setSmallCamQuality(luid) {
  setCamToLD(luid);
}
export async function setBigScreenQuality(luid) {
  let numBigs = selectBigLuids(getState()).length;
  if (numBigs > 2) setScreenToLD(luid);
  else setScreenToHD(luid);
}

export async function setSmallScreenQuality(luid) {
  setScreenToLD(luid);
}

async function setCamToLD(luid) {
  betaSetVideoReceiveQuality(sessionIds[luid], { layer: 0 }, "cam");
}
async function setCamToMD(luid) {
  betaSetVideoReceiveQuality(sessionIds[luid], { layer: 1 }, "cam");
}
async function setCamToHD(luid) {
  betaSetVideoReceiveQuality(sessionIds[luid], { layer: 2 }, "cam");
}
async function setScreenToLD(luid) {
  betaSetVideoReceiveQuality(sessionIds[luid], { layer: 0 }, "screen");
}
async function setScreenToHD(luid) {
  betaSetVideoReceiveQuality(sessionIds[luid], { layer: 1 }, "screen");
}

// approximation of a new API method we are testDummyMedia. `priority` argument is ignored
// for now except in Daily dev/staging environments
// type === "cam" || "screen"
async function betaSetVideoReceiveQuality(
  sessionId,
  { layer, priority } = {},
  type
) {
  window.efs_debug &&
    window.log(
      "beta receive quality function",
      sessionId,
      layer,
      priority,
      type
    );
  if (!window.rtcpeers || !window.rtcpeers.sfu) {
    window.efs_debug && window.log("sfu not set up yet");
    return;
  }
  try {
    let c = window.rtcpeers.sfu.consumers[sessionId + "/" + type + "-video"];
    if (!c) {
      window.efs_debug &&
        window.log("no consumer found for participant", sessionId);
      return;
    }
    if (c._preferredLayer === layer) {
      window.efs_debug && window.log("PreferredLayer Hit");
    } else {
      window.efs_debug && window.log("PreferredLayer Miss");
      await window.rtcpeers.sfu.setConsumerLayer(c, layer, priority);
    }
  } catch (e) {
    console.error(e);
  }
}
//#endregion

//#region // Device Selections

export async function setAudioInputDevice(deviceID) {
  try {
    if (!deviceID) throw new Error();

    // seems to glitch if this is not true
    if (selectMeetingLive(getState())) {
      window.efs_debug &&
        window.log("will setInputDevicesAsync for AudioInputDevice Only");
      await callFrame.setInputDevicesAsync({ audioDeviceId: deviceID });
    }

    dispatch(setCurrentMic(deviceID));
    window.efs_debug && window.log("Successfully set audio input device");
  } catch (e) {
    window.efs_debug && window.log("Could not set audio input device async");
  }
}

export async function setCameraInputDevice(deviceID) {
  try {
    if (!deviceID) throw new Error();
    window.efs_debug && window.log("Found deviceId for set video input device");

    // seems to glitch if this is not true
    if (selectMeetingLive(getState())) {
      window.efs_debug &&
        window.log("will setInputDevicesAsync for VideoDevice Only");
      await callFrame.setInputDevicesAsync({ videoDeviceId: deviceID });
    }

    dispatch(setCurrentCamera(deviceID));
    window.efs_debug && window.log("Successfully set video input device");
  } catch (e) {
    window.efs_debug && window.log("Could not set video input device async");
  }
}

export function setAudioOutputDevice(deviceID) {
  if (deviceID) {
    if (selectMeetingLive(getState()))
      callFrame.setOutputDevice({ outputDeviceId: deviceID });

    dispatch(setCurrentSpeakers(deviceID));
    window.efs_debug && window.log("Successfully set audio output device");
  }
}

export async function setDailyDevices() {
  if (callFrame && selectMeetingLive(getState())) {
    window.efs_debug && window.log("will setInputDevicesAsync for all devices");
    await callFrame.setInputDevicesAsync({
      audioDeviceId: selectCurrentMic(getState()),
      videoDeviceId: selectCurrentCamera(getState()),
    });
    callFrame.setOutputDevice({
      outputDeviceId: selectCurrentSpeakers(getState()),
    });
    window.efs_debug &&
      window.log("Successfully set videoIn audioIn and audioOut devices");
  }
}

//#endregion

export default slice.reducer;
